import { PropsWithChildren, type JSX } from 'react';
import { useTranslations } from 'next-intl';
import Close from '../../visuals/Icons/Old/Close';
import Button from '../../components/Button';

interface ISidePanelHeaderProps {
  onClose: () => void;
  UNSAFE_className?: string;
}

const SidePanelHeader = ({
  children,
  onClose,
  UNSAFE_className = '',
}: PropsWithChildren<ISidePanelHeaderProps>): JSX.Element | null => {
  const tGlobal = useTranslations('Global');

  return (
    <header
      className={`side-panel-header ${UNSAFE_className}
      tw-flex tw-h-16 tw-items-center tw-justify-between tw-border-0 tw-border-b tw-border-solid tw-border-blue-200 tw-bg-white tw-px-4 tw-py-3`}
    >
      <div
        className="title-container tw-mr-3 tw-flex tw-min-w-0 tw-gap-3"
        data-testid="side-panel-title"
      >
        {children}
      </div>

      <Button
        variant="clear"
        className="close-icon tw-size-11 tw-translate-y-0.5"
        onClick={onClose}
        ariaLabel={tGlobal('close')}
      >
        <Close width={12} height={12} aria-hidden />
      </Button>
    </header>
  );
};

interface ISidePanelBodyProps {
  withScrollDisabled?: boolean;
  UNSAFE_className?: string;
  dataTestId?: string;
}

const SidePanelFooter = ({
  children,
  UNSAFE_className = '',
}: PropsWithChildren<ISidePanelBodyProps>): JSX.Element | null => {
  return (
    <div
      className={`side-panel-footer ${UNSAFE_className}
      tw-border-0 tw-border-t tw-border-solid tw-border-blue-200 tw-bg-white tw-p-4`}
    >
      {children}
    </div>
  );
};

const SidePanelBody = ({
  children,
  withScrollDisabled = false,
  UNSAFE_className = '',
  dataTestId = 'side-panel-body',
}: PropsWithChildren<ISidePanelBodyProps>): JSX.Element | null => {
  return (
    <div
      className={`side-panel-body ${UNSAFE_className} tw-mb-16 tw-grow tw-px-6 tw-py-8 ${
        withScrollDisabled ? '' : 'tw-overflow-y-auto'
      }`}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
};

interface ISidePanelProps {
  isOpen?: boolean;
  onClose: () => void;
  UNSAFE_className?: string;
}

const SidePanel = ({
  children,
  isOpen = false,
  onClose,
  UNSAFE_className = '',
}: PropsWithChildren<ISidePanelProps>): JSX.Element | null => {
  return (
    <>
      {isOpen && (
        <div
          className={`side-panel-overlay
            no-doc-scroll tw-fixed tw-right-0 tw-top-0 tw-z-sidePanelBackdrop
            tw-h-full tw-w-full tw-bg-overlay
          `}
          onClick={() => onClose()}
          data-testid="side-panel"
        />
      )}
      <section
        className={`side-panel-container ${UNSAFE_className}
        tw-fixed tw-bottom-0 tw-right-0 tw-z-sidePanel tw-flex tw-h-full tw-w-[465px] tw-max-w-full
        tw-transform tw-flex-col tw-overflow-hidden
        tw-bg-white
        tw-transition-all tw-duration-300 tw-ease-out min-[465px]:tw-rounded-l-2xl
        ${
          isOpen
            ? 'tw-visible max-[465px]:tw-translate-y-0 min-[465px]:tw-translate-x-0'
            : 'tw-invisible max-[465px]:tw-translate-y-full min-[465px]:tw-translate-x-full'
        }
        `}
      >
        {isOpen && <>{children}</>}
      </section>

      <style jsx>{`
        :global(#botmind-iframe) {
          ${isOpen ? 'display: none !important;' : ''}
        }

        :global([id^='cobbai-']) {
          ${isOpen ? 'display: none !important;' : ''}
        }
      `}</style>
    </>
  );
};

export default { SidePanel, SidePanelHeader, SidePanelBody, SidePanelFooter };
